import { Alert, Button, SegmentedControl, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { TestType } from '@prisma/client';
import { IconAlertTriangle, IconCopy, IconDeviceDesktop, IconDeviceMobile } from '@tabler/icons-react';
import { useState } from 'react';
import { api } from '~/trpc-extra/api';

// Only WEB and WEB_MOBILE support viewport switching
type ViewportTestType = typeof TestType.WEB | typeof TestType.WEB_MOBILE;

type DuplicateTestModalProps = {
  testId: string;
  projectId: string;
  testName: string;
  currentTestType: ViewportTestType;
};

const VIEWPORT_OPTIONS = [
  { value: TestType.WEB, label: 'Desktop' },
  { value: TestType.WEB_MOBILE, label: 'Mobile' },
] as const;

function DuplicateTestModal({ testId, projectId, testName, currentTestType }: DuplicateTestModalProps) {
  const [selectedViewport, setSelectedViewport] = useState<ViewportTestType>(currentTestType);

  const { mutateAsync: duplicateTest, isPending } = api.test.duplicate.useMutation({
    onSuccess: (newTestId) => {
      notifications.show({
        color: 'teal',
        message: 'Test duplicated successfully',
        icon: <IconCopy size={16} />,
      });
      const newTestUrl = `/project/${projectId}/test/${newTestId}`;
      window.open(newTestUrl, '_blank', 'noreferrer');
    },
    onError: (error) => {
      console.error(error);
      notifications.show({
        color: 'red',
        title: 'Unable to Duplicate',
        message: 'Please contact support',
        icon: <IconAlertTriangle size={16} />,
      });
    },
  });

  const viewportChanging = selectedViewport !== currentTestType;

  const handleDuplicate = async () => {
    notifications.show({
      color: 'teal',
      message: 'Duplicating test...',
      icon: <IconCopy size={16} />,
    });
    modals.closeAll();

    await duplicateTest({
      id: testId,
      testType: viewportChanging ? selectedViewport : undefined,
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <Text
        size="sm"
        c="dimmed"
      >
        Create a copy of &quot;{testName}&quot; with the selected viewport.
      </Text>

      <div>
        <Text
          size="sm"
          fw={500}
          mb="xs"
        >
          Viewport
        </Text>
        <SegmentedControl
          fullWidth
          value={selectedViewport}
          onChange={(value) => {
            if (value === TestType.WEB || value === TestType.WEB_MOBILE) {
              setSelectedViewport(value);
            }
          }}
          data={VIEWPORT_OPTIONS.map((option) => ({
            value: option.value,
            label: (
              <div className="flex items-center justify-center gap-2 py-1">
                {option.value === TestType.WEB ?
                  <IconDeviceDesktop size={16} />
                : <IconDeviceMobile size={16} />}
                <span>{option.label}</span>
              </div>
            ),
          }))}
        />
      </div>

      {!!viewportChanging && (
        <Alert
          color="yellow"
          icon={<IconAlertTriangle size={16} />}
          title="Viewport change"
        >
          Changing viewport will clear all screenshots and element position data from the duplicated test. You may need
          to re-record some steps.
        </Alert>
      )}

      <div className="flex flex-row justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => modals.closeAll()}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          leftSection={<IconCopy size={16} />}
          loading={isPending}
          onClick={handleDuplicate}
        >
          Duplicate
        </Button>
      </div>
    </div>
  );
}

/**
 * Opens a modal to duplicate a test with viewport selection.
 * Only call this for WEB or WEB_MOBILE tests - MOBILE_APP tests should be duplicated directly.
 */
export function openDuplicateTestModal(props: DuplicateTestModalProps & { zIndex?: number }) {
  modals.open({
    title: <span className="text-base font-semibold">Duplicate Test</span>,
    size: 'md',
    children: <DuplicateTestModal {...props} />,
    zIndex: props.zIndex,
  });
}
